/**
 * useSupervisedPaperReadiness — polls /api/v1/readiness/supervised-paper-gate.
 *
 * Read-only. Surfaces the live S26/S27 gate state so the banner can render
 * the actual posture instead of a hardcoded "NOT_READY" string. Failsafe:
 * any non-2xx, network error, or schema mismatch keeps the last good
 * render and falls back to the conservative "BLOCKED" rendering — the UI
 * NEVER claims paper trading is enabled unless the backend explicitly says
 * `paper_trading_allowed=true` AND `live_trading_allowed=false`.
 *
 * The hook does NOT send credentials, does NOT POST anything, and does
 * NOT mutate any broker / queue / runner state.
 */
import { useEffect, useRef, useState } from 'react';
import { apiV1Base } from '../lib/apiConfig';

export type SupervisedPaperGateStatus =
  | 'NOT_READY'
  | 'READY_SUPERVISED_PAPER_ONLY';

export type ExecutionPosture =
  | 'EXECUTION_BLOCKED'
  | 'EXECUTION_ALLOWED'
  | 'UNKNOWN';

export interface SupervisedPaperReadiness {
  status: SupervisedPaperGateStatus;
  paper_trading_allowed: boolean;
  live_trading_allowed: boolean;
  execution_posture: ExecutionPosture;
  paper_readiness_blockers: string[];
  paper_study_warnings: string[];
  live_trading_blockers: string[];
  strategy_not_proven_warnings: string[];
  required_owner_action: string;
}

export interface UseSupervisedPaperReadinessResult {
  data: SupervisedPaperReadiness | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const DEFAULT_POLL_MS = 60_000;

function normalizeGateStatus(raw: unknown): SupervisedPaperGateStatus {
  return raw === 'READY_SUPERVISED_PAPER_ONLY'
    ? 'READY_SUPERVISED_PAPER_ONLY'
    : 'NOT_READY';
}

function normalizeExecutionPosture(raw: unknown): ExecutionPosture {
  if (raw === 'EXECUTION_BLOCKED') return 'EXECUTION_BLOCKED';
  if (raw === 'EXECUTION_ALLOWED') return 'EXECUTION_ALLOWED';
  return 'UNKNOWN';
}

function normalizeStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseReadiness(payload: unknown): SupervisedPaperReadiness | null {
  if (!payload || typeof payload !== 'object') return null;
  const env = payload as Record<string, unknown>;
  const data = (env.data ?? env) as Record<string, unknown>;
  if (!data || typeof data !== 'object') return null;
  return {
    status: normalizeGateStatus(data.supervised_paper_gate_status),
    paper_trading_allowed: data.paper_trading_allowed === true,
    live_trading_allowed: data.live_trading_allowed === true,
    execution_posture: normalizeExecutionPosture(data.execution_posture),
    paper_readiness_blockers: normalizeStringArray(data.paper_readiness_blockers),
    paper_study_warnings: normalizeStringArray(data.paper_study_warnings),
    live_trading_blockers: normalizeStringArray(data.live_trading_blockers),
    strategy_not_proven_warnings: normalizeStringArray(
      data.strategy_not_proven_warnings,
    ),
    required_owner_action:
      typeof data.required_owner_action === 'string'
        ? data.required_owner_action
        : '',
  };
}

export function useSupervisedPaperReadiness(
  pollMs: number = DEFAULT_POLL_MS,
): UseSupervisedPaperReadinessResult {
  const [data, setData] = useState<SupervisedPaperReadiness | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    let aborted = false;
    const controller = new AbortController();
    const url = `${apiV1Base()}/readiness/supervised-paper-gate`;

    async function fetchOnce() {
      try {
        const resp = await fetch(url, {
          method: 'GET',
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!resp.ok) {
          throw new Error(`HTTP ${resp.status}`);
        }
        const json = await resp.json();
        const parsed = parseReadiness(json);
        if (!parsed) throw new Error('schema mismatch');
        if (aborted || !mountedRef.current) return;
        setData(parsed);
        setError(null);
      } catch (e: unknown) {
        if (aborted || !mountedRef.current) return;
        const msg = e instanceof Error ? e.message : String(e);
        // Do not clobber last good render on transient failure.
        setError(msg);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }

    fetchOnce();

    let interval: number | null = null;
    if (pollMs > 0) {
      interval = window.setInterval(fetchOnce, pollMs);
    }

    return () => {
      aborted = true;
      controller.abort();
      if (interval !== null) window.clearInterval(interval);
    };
  }, [pollMs, tick]);

  return {
    data,
    loading,
    error,
    refresh: () => setTick((t) => t + 1),
  };
}

/**
 * `isSupervisedPaperEnabled` — the single, conservative predicate that
 * decides whether the UI may render "SUPERVISED PAPER ENABLED". Returns
 * true ONLY when the backend explicitly affirms paper_trading_allowed AND
 * explicitly denies live_trading_allowed. Any other combination — loading,
 * error, schema mismatch, or live_trading_allowed=true — falls through to
 * the conservative blocked render.
 */
export function isSupervisedPaperEnabled(
  d: SupervisedPaperReadiness | null,
): boolean {
  if (!d) return false;
  return d.paper_trading_allowed === true && d.live_trading_allowed === false;
}
